import type { Distance, Pixel, Rectangle } from "@pdfking/renderer-api"

import { PDFRasterize } from "@pdfking/renderer-default"
import { First, IsNull, Range } from "@pdfking/tfunc"

import type { PdfEngine } from "../../types/engine/PdfEngine"
import type { PdfEngineOptions } from "../../types/engine/PdfEngineOptions"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskRasterizeOptions } from "../../types/tasks/options/TaskRasterizeOptions"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"

import { GenerateFile } from "../../files/GenerateFile"
import { RequestBlob } from "../../files/RequestBlob"
import { ConvertRectangle } from "../../geometry/ConvertRectangle"
import { ContentType } from "../../types/files/ContentType"
import { VirtualFileType } from "../../types/files/VirtualFileType"
import { TaskName } from "../../types/tasks/TaskName"
import { TrackedTaskName } from "../../types/tasks/tracking/TrackedTaskName"

const computeRasterResolution = (
  options: PdfEngineOptions,
  media_box: Rectangle<Distance>
): Pixel => {
  const { image_resolution, max_size } = options
  if (IsNull(max_size)) return image_resolution
  const max_dimension = Math.max(media_box.width, media_box.height)
  return ((max_size / max_dimension) * 72) as Pixel
}

export async function* TaskRasterize(
  engine: PdfEngine,
  task: TaskRasterizeOptions,
  emit: TrackingCallback
): AsyncGenerator<TaskResult> {
  void emit({
    done: 0,
    name: TrackedTaskName.RASTERIZE_PDF,
    total: 1,
  })

  const { size, stream } = await RequestBlob(engine, task.source, emit)

  const { blob, size: rasterSize } = await First(
    PDFRasterize(
      { size, stream, type: ContentType.PDF },
      computeRasterResolution(task.options, task.source.media_box),
      page_count => {
        if (page_count !== 1) {
          throw new Error(`Expected 1 page, got ${page_count}`)
        }
        return Range(1, 1)
      }
    )
  )

  const content = await GenerateFile(engine, [blob], ContentType.PNG, emit)

  yield {
    content: {
      content,
      enum: VirtualFileType.SINGLE_PAGE_IMAGE,
      file_meta: {},
      size: ConvertRectangle(task.options, rasterSize),
      source: task.source,
    },
    enum: TaskName.CONTENT_FILE,
    name: "img.png",
  } as TaskResult

  void emit({
    done: 1,
    name: TrackedTaskName.RASTERIZE_PDF,
    total: 1,
  })
}
